import React from "react";
import { CircleCheckIcon, CircleDashedIcon, CircleMinusIcon, FileTextIcon, BoxIcon } from "lucide-react";
import { SAMPLE_MEMO, CriterionStatus } from "../../data/sampleMemo";
import { cn } from "../../src/lib/utils";
type MemoDocumentProps = {
  /** "preview": hero card (condensed). "full": complete memo for reading. */
  variant?: 'preview' | 'full';
  className?: string;
  /** Heading level for the memo title — keeps one H1 per page. */
  titleAs?: 'h2' | 'h3';
};
const STATUS: Record<CriterionStatus, {
  label: string;
  icon: typeof CircleCheckIcon;
  className: string;
}> = {
  met: {
    label: 'Met',
    icon: CircleCheckIcon,
    className: 'text-[#166534]'
  },
  partial: {
    label: 'Partial',
    icon: CircleMinusIcon,
    className: 'text-[#92400E]'
  },
  'not-met': {
    label: 'Not met',
    icon: CircleDashedIcon,
    className: 'text-[#9F1239]'
  },
  unknown: {
    label: 'Unknown',
    icon: BoxIcon,
    className: 'text-text-tertiary'
  }
};

/** Readable investment memo built from the fictional SAMPLE_MEMO. */
export function MemoDocument({
  variant = 'full',
  className,
  titleAs = 'h2'
}: MemoDocumentProps) {
  const preview = variant === 'preview';
  const memo = SAMPLE_MEMO;
  const Title = titleAs;
  const Sub = titleAs === 'h2' ? 'h3' : 'h4';
  const criteria = preview ? memo.thesisFit.criteria.slice(0, 4) : memo.thesisFit.criteria;
  const strengths = preview ? memo.strengths.slice(0, 2) : memo.strengths;
  const risks = preview ? memo.risks.slice(0, 2) : memo.risks;
  return <article aria-label={`Sample investment memo: ${memo.company} (fictional)`} className={cn('overflow-hidden rounded-2xl border border-hairline bg-panel text-left shadow-[0_30px_70px_-45px_rgba(32,22,95,0.35)]', className)} data-id="element-799" data-mp="3fsxscn8hja9v">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-hairline-soft bg-surface px-5 py-3" data-id="element-800" data-mp="35xctgxypqq4c">
        <span className="inline-flex items-center gap-2 text-xs font-semibold text-text-secondary" data-id="element-801" data-mp="1ufuyamv76ytf">
          <FileTextIcon className="h-3.5 w-3.5 text-lime" aria-hidden="true" data-id="element-802" data-mp="xw1eezvl558" />
          Investment memo · Draft for review
        </span>
        <span className="rounded-md border border-[#F0E0C4] bg-[#FDF7ED] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-[#92400E]" data-id="element-803" data-mp="3s478osvhb250">
          Illustrative example
        </span>
      </header>

      <div className={cn('flex flex-col', preview ? 'gap-5 p-5' : 'gap-8 p-5 sm:p-8')} data-id="element-804" data-mp="3idm3unp7p1dn">
        <div data-id="element-805" data-mp="3dgcc31ecodmn">
          <Title className={cn('font-semibold tracking-[-0.02em] text-text-primary', preview ? 'text-lg' : 'text-2xl')} data-id="element-806" data-mp="3dcolob47c7c5">
            {memo.company} <span className="font-normal text-text-tertiary" data-id="element-807" data-mp="34dkzgy9qnbtf">(fictional)</span>
          </Title>
          <p className="mt-1 text-sm text-text-secondary" data-id="element-808" data-mp="2zf9t4xinjjfy">{memo.oneLiner}</p>
          <p className="mt-2 flex flex-wrap gap-1.5" data-id="element-809" data-mp="2zhcqy3q1qgv8">
            {memo.meta.map(item => <span key={item} className="rounded-md bg-elevated px-2 py-0.5 text-[11px] font-medium text-text-secondary" data-id="element-810" data-mp="12lyfhgo9kh6g">
                {item}
              </span>)}
          </p>
        </div>

        {!preview ? <MemoBlock title="Company overview" as={Sub} data-id="element-811" data-mp="1553abpff9v7x">
            <p className="text-sm leading-relaxed text-text-secondary" data-id="element-812" data-mp="1a57eqh322zhs">{memo.overview}</p>
          </MemoBlock> : null}

        <MemoBlock title="Investment thesis fit" as={Sub} aside={memo.thesisFit.summary} data-id="element-813" data-mp="1exhd8li8l7vo">
          <ul className={cn('grid gap-1.5', !preview && 'sm:grid-cols-2')} data-id="element-814" data-mp="1juqzjsabx3fa">
            {criteria.map(criterion => {
            const status = STATUS[criterion.status];
            const Icon = status.icon;
            return <li key={criterion.label} className="flex items-start gap-2 rounded-lg border border-hairline-soft bg-surface px-3 py-2" data-id="element-815" data-mp="r7wplpb51mg2">
                  <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', status.className)} aria-hidden="true" data-id="element-816" data-mp="115kl4hgkixza" />
                  <span className="min-w-0 flex-1" data-id="element-817" data-mp="27lwocwy8z61q">
                    <span className="flex items-baseline justify-between gap-2" data-id="element-818" data-mp="27l4x1ybonuh9">
                      <span className="text-[13px] font-medium text-text-primary" data-id="element-819" data-mp="2cjeopyy78cy4">{criterion.label}</span>
                      <span className={cn('shrink-0 text-[11px] font-semibold', status.className)} data-id="element-820" data-mp="2a6uh9wlydvzl">{status.label}</span>
                    </span>
                    <span className="block text-xs text-text-secondary" data-id="element-821" data-mp="2k48uzoghs6o7">
                      {criterion.detail}
                      {!preview && criterion.source !== '—' ? <span className="text-text-tertiary" data-id="element-822" data-mp="2f3ktg8un07fq"> · {criterion.source}</span> : null}
                    </span>
                  </span>
                </li>;
          })}
          </ul>
          {!preview ? <p className="mt-2 text-xs text-text-tertiary" data-id="element-823" data-mp="2p2tuflnx6mbl">{memo.thesisFit.note}</p> : null}
        </MemoBlock>

        <div className={cn('grid gap-5', !preview && 'md:grid-cols-2')} data-id="element-824" data-mp="32tyl3y9b849s">
          <MemoBlock title="Key strengths" as={Sub} data-id="element-825" data-mp="2xwyglrs3z9ks">
            <SourcedList items={strengths} tone="strength" data-id="element-826" data-mp="3csxop2f7x9px" />
          </MemoBlock>
          <MemoBlock title="Risks" as={Sub} data-id="element-827" data-mp="3cqthaai41skw">
            <SourcedList items={risks} tone="risk" data-id="element-828" data-mp="37umeuroxaz7p" />
          </MemoBlock>
        </div>

        {!preview ? <MemoBlock title="Unanswered questions" as={Sub} data-id="element-829" data-mp="3rfd580mueg04">
            <ol className="flex list-decimal flex-col gap-1.5 pl-5 text-sm text-text-secondary marker:text-text-tertiary" data-id="element-830" data-mp="3rds6dythwawg">
              {memo.openQuestions.map(question => <li key={question} data-id="element-831" data-mp="3hgxcjt862yoi">{question}</li>)}
            </ol>
          </MemoBlock> : <p className="rounded-lg border border-[#F0E0C4] bg-[#FDF7ED] px-3 py-2 text-xs text-[#92400E]" data-id="element-832" data-mp="3mfh01y2esiiz">
            {memo.openQuestions.length} unanswered questions flagged for founder follow-up
          </p>}

        <MemoBlock title="Financial snapshot" as={Sub} aside="Company-provided, unverified" data-id="element-833" data-mp="3cj5mz5uzvonh">
          <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-id="element-834" data-mp="2ys0klw08si9g">
            {memo.financials.map(metric => <div key={metric.label} className="rounded-lg border border-hairline-soft bg-surface px-3 py-2" data-id="element-835" data-mp="33r3nb9sr2ce9">
                <dt className="text-[11px] text-text-tertiary" data-id="element-836" data-mp="2ou2x5aq7iphw">{metric.label}</dt>
                <dd className="mt-0.5 font-mono text-[15px] font-semibold text-text-primary" data-id="element-837" data-mp="2tqt8jkr2gwwz">{metric.value}</dd>
                {!preview ? <dd className="text-[11px] text-text-tertiary" data-id="element-838" data-mp="2aosqng7whfhi">{metric.note}</dd> : null}
              </div>)}
          </dl>
        </MemoBlock>

        {!preview ? <div className="grid gap-5 md:grid-cols-2" data-id="element-839" data-mp="20wws641wjncr">
            <MemoBlock title="Sources reviewed" as={Sub} data-id="element-840" data-mp="20xpu8rpx1ook">
              <ul className="flex flex-col gap-1 text-sm text-text-secondary" data-id="element-841" data-mp="1vz4suoyxgyyj">
                {memo.sources.map(source => <li key={source} data-id="element-842" data-mp="2tl2euwcinmf8">· {source}</li>)}
              </ul>
            </MemoBlock>
            <MemoBlock title="For analyst judgment" as={Sub} data-id="element-843" data-mp="2omr14aj3orrq">
              <ul className="flex flex-col gap-1 text-sm text-text-secondary" data-id="element-844" data-mp="2jnxpyoradcuq">
                {memo.analystJudgment.map(item => <li key={item} data-id="element-845" data-mp="2ep35zjhl1i91">· {item}</li>)}
              </ul>
            </MemoBlock>
          </div> : null}
      </div>
    </article>;
}
function MemoBlock({
  title,
  as: Tag,
  aside,
  children
}: {
  title: string;
  as: 'h3' | 'h4';
  aside?: string;
  children: React.ReactNode;
}) {
  return <section data-id="element-846" data-mp="3exb1ekxc138o">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2" data-id="element-847" data-mp="3exjdialms4it">
        <Tag className="text-[11px] font-semibold uppercase tracking-[0.1em] text-text-secondary" data-id="element-848" data-mp="3jvupaqvhkp3o">{title}</Tag>
        {aside ? <span className="text-[11px] text-text-tertiary" data-id="element-849" data-mp="3ouxj1f60n8wc">{aside}</span> : null}
      </div>
      {children}
    </section>;
}
function SourcedList({
  items,
  tone
}: {
  items: readonly {
    text: string;
    source: string;
  }[];
  tone: 'strength' | 'risk';
}) {
  return <ul className="flex flex-col gap-2" data-id="element-850" data-mp="6h7s6aqkn6i8">
      {items.map(item => <li key={item.text} className="flex gap-2 text-[13px] leading-relaxed text-text-primary" data-id="element-851" data-mp="6fmzkqnthxn0">
          <span aria-hidden="true" className={cn('mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full', tone === 'strength' ? 'bg-[#16A34A]' : 'bg-[#D97706]')} data-id="element-852" data-mp="bgu6yom1sut5" />
          <span data-id="element-853" data-mp="2d87b3bg6yyo5">
            {item.text}
            <span className="block text-[11px] text-text-tertiary" data-id="element-854" data-mp="2d98o3lgrpv8i">Source: {item.source}</span>
          </span>
        </li>)}
    </ul>;
}